import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { routes } from "~/constants";
import { useAuthN } from "~/hooks/useAuthN";
import { useCurrentUser } from "~/hooks/useCurrentUser";
import { trackActivate as track } from "~/OnboardingApp/events";
import { usePartner } from "~/OnboardingApp/hooks";
import { useTrialOffersUpdateMutation } from "~/store/api";

interface UseActivationSkip {
  isRedeeming: boolean;
}

const SKIP_ROUTE = `${routes.setPassword}?partner=true`;

// Auto-redeems the partner trial at the activation step — the point at which we
// are ready to skip manual activation-code entry. Redemption is DELIBERATELY
// deferred to here rather than fired at sign-up: a scanner should not get a
// trial before progressing through onboarding to this page.
//
// The signal is server-side (currentUser.partner), NOT the browser: the opaque
// qr_code_key is cleared from sessionStorage right after signup, so by the time
// onboarding reaches this page the client no longer holds it. `activationPending`
// means the user carried a still-redeemable activation code (recorded on their
// onboarding source); we then redeem through the same PATCH /trial_offers path
// the manual form uses — with an empty body, so the server resolves the carried
// code itself — and hand the user to set-password, firing the same funnel events
// so the skip path matches the manual path. `activatedAt` means a prior visit
// already redeemed, so we skip straight through without minting a second offer.
//
// isRedeeming keeps ActivatePartnerPage on a loader until we navigate away so
// the manual code card never flashes mid-redemption; a failed auto-redeem drops
// it to false so the manual card renders as the fallback. A one-shot ref bounds
// the redemption/tracking to exactly once per mount even though the effect's
// deps can change on the same mount (e.g. the mount refresh updating the user).
export const useActivationSkip = (): UseActivationSkip => {
  const navigate = useNavigate();
  const { refreshCurrentUser } = useAuthN();
  const currentUser = useCurrentUser();
  const { partnerSlug } = usePartner();
  const [apiTrialOffersUpdate] = useTrialOffersUpdateMutation();

  const alreadyRedeemed = Boolean(currentUser?.partner?.activatedAt);
  const activationPending = Boolean(currentUser?.partner?.activationPending);
  // Start on the loader whenever we intend to skip so the manual card does not
  // flash before the effect navigates.
  const [isRedeeming, setIsRedeeming] = useState(
    alreadyRedeemed || activationPending,
  );
  const hasRun = useRef(false);

  // Refresh once on mount so a redemption completed on a prior visit (the user
  // navigated back to this page) is observed from the cached user.
  useEffect(() => {
    refreshCurrentUser();
  }, [refreshCurrentUser]);

  useEffect(() => {
    if (hasRun.current) return;

    // No carried trial and no prior redemption: manual entry only. Do not latch
    // (no hasRun) so a mount refresh that reveals a pending trial re-evaluates.
    if (!alreadyRedeemed && !activationPending) {
      setIsRedeeming(false);
      return;
    }

    hasRun.current = true;
    // Re-arm the loader: on a first render with no cached user, isRedeeming
    // initialized to false; a mount refresh can then reveal the pending trial
    // and route us here, and we must not flash the manual card before skipping.
    setIsRedeeming(true);

    const finishSkip = () => {
      track.submitActivationCode();
      track.partnerCodeRedeemed(partnerSlug);
      navigate(SKIP_ROUTE);
    };

    // Already redeemed on a prior visit — skip without minting a second offer.
    if (alreadyRedeemed) {
      finishSkip();
      return;
    }

    (async () => {
      try {
        // Empty body: the server resolves the carried activation code from the
        // user's onboarding source.
        await apiTrialOffersUpdate({}).unwrap();
        await refreshCurrentUser();
        finishSkip();
      } catch (err) {
        // Redemption failed — fall back to the manual code card so the scanner
        // can still activate by typing the code.
        Sentry.captureException(err);
        setIsRedeeming(false);
      }
    })();
  }, [
    alreadyRedeemed,
    activationPending,
    partnerSlug,
    navigate,
    apiTrialOffersUpdate,
    refreshCurrentUser,
  ]);

  return { isRedeeming };
};
